import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaGraduationCap, FaArrowLeft, FaSearch, FaSort } from 'react-icons/fa';
import { academyAPI } from '../../services/api';
import './AcademyApplications.css';
import { getCountryName } from '../../utils/locationUtils';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../config/constants';

function AcademyApplications() {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('submissionDate');
    const [sortOrder, setSortOrder] = useState('desc');
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        waitlist: 0,
        byCountry: {},
        byEducationLevel: {},
        recentApplications: [],
        averageAge: 0,
        minAge: 0,
        maxAge: 0,
        genderDistribution: {},
        topFieldsOfStudy: [],
        acceptanceRate: 0
    });
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    
    // Check if user can delete applications (only admin and super admin)
    const canDeleteApplications = currentUser?.role === ROLES.ADMIN || currentUser?.role === ROLES.SUPER_ADMIN;

    useEffect(() => {
        fetchData();
    }, []);

    const calculateStats = (applications) => {
        const newStats = {
            total: applications.length,
            pending: 0,
            approved: 0,
            rejected: 0,
            waitlist: 0,
            byCountry: {},
            byEducationLevel: {},
            recentApplications: [],
            ages: [],
            genderDistribution: {},
            fieldOfStudyCounts: {},
            topFieldsOfStudy: [],
            acceptanceRate: 0
        };

        let consideredForAcceptanceRate = 0;

        applications.forEach(app => {
            const status = app.status || 'pending';
            newStats[status] = (newStats[status] || 0) + 1;

            if (app.country) {
                newStats.byCountry[app.country] = (newStats.byCountry[app.country] || 0) + 1;
            }
            if (app.educationLevel) {
                newStats.byEducationLevel[app.educationLevel] = 
                    (newStats.byEducationLevel[app.educationLevel] || 0) + 1;
            }

            // Age calculation
            if (app.dateOfBirth) {
                const age = calculateAge(app.dateOfBirth);
                if (typeof age === 'number' && !isNaN(age)) {
                    newStats.ages.push(age);
                }
            }

            // Gender distribution
            if (app.gender) {
                const gender = app.gender.trim() || 'Not specified';
                newStats.genderDistribution[gender] = (newStats.genderDistribution[gender] || 0) + 1;
            }

            // Field of study counts
            if (app.fieldOfStudy) {
                const field = app.fieldOfStudy.trim();
                if (field) {
                    newStats.fieldOfStudyCounts[field] = (newStats.fieldOfStudyCounts[field] || 0) + 1;
                }
            }
            
            if (status !== 'pending') {
                consideredForAcceptanceRate++;
            }
        });

        // Calculate age statistics
        if (newStats.ages.length > 0) {
            newStats.averageAge = Math.round(newStats.ages.reduce((sum, age) => sum + age, 0) / newStats.ages.length);
            newStats.minAge = Math.min(...newStats.ages);
            newStats.maxAge = Math.max(...newStats.ages);
        } else {
            newStats.averageAge = 'N/A';
            newStats.minAge = 'N/A';
            newStats.maxAge = 'N/A';
        }
        delete newStats.ages;

        // Determine top fields of study
        newStats.topFieldsOfStudy = Object.entries(newStats.fieldOfStudyCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([field, count]) => ({ field, count }));
        delete newStats.fieldOfStudyCounts;

        // Calculate acceptance rate
        if (consideredForAcceptanceRate > 0) {
            newStats.acceptanceRate = parseFloat(((newStats.approved / consideredForAcceptanceRate) * 100).toFixed(1));
        } else {
            newStats.acceptanceRate = 0;
        }
        
        newStats.recentApplications = [...applications]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5);

        return newStats;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await academyAPI.getAllApplications();
            const applications = Array.isArray(response) ? response : [];
            setApplications(applications);
            setStats(calculateStats(applications));
            setLoading(false);
        } catch (error) {
            setError('Failed to fetch applications');
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (applicationId, newStatus) => {
        try {
            await academyAPI.updateApplicationStatus(applicationId, newStatus);
            await fetchData(); // Refresh data
            if (selectedApplication?.id === applicationId) {
                setSelectedApplication(null);
            }
            alert(`Application status updated to ${newStatus}`);
        } catch (error) {
            setError(error.message || 'Failed to update application status');
        }
    };

    const handleDeleteApplication = async (applicationId) => {
        // Only allow admins to delete applications
        if (!canDeleteApplications) {
            alert('You do not have permission to delete applications');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
            return;
        }

        try {
            await academyAPI.deleteApplication(applicationId);
            await fetchData(); // Refresh data
            if (selectedApplication?.id === applicationId) {
                setSelectedApplication(null);
            }
            alert('Application deleted successfully');
        } catch (error) {
            setError(error.message || 'Failed to delete application');
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return 'N/A';
        const birthDate = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        
        return age;
    };

    const renderStats = () => (
        <div className="academy-stats-dashboard">
            <div className="academy-stats-header">
                <h2>Applications Overview</h2>
                <div className="academy-stats-period">
                    Total Applications: {stats.total}
                </div>
            </div>

            <div className="academy-stats-grid">
                <div className="academy-stat-card">
                    <div className="academy-stat-title">Status Breakdown</div>
                    <div className="academy-stat-content">
                        <div className="academy-stat-item">
                            <span className="academy-stat-label">Pending</span>
                            <span className="academy-stat-value status-pending">
                                <span className="academy-stat-value-indicator status-pending"></span>
                                {stats.pending}
                            </span>
                        </div>
                        <div className="academy-stat-item">
                            <span className="academy-stat-label">Approved</span>
                            <span className="academy-stat-value status-approved">
                                <span className="academy-stat-value-indicator status-approved"></span>
                                {stats.approved}
                            </span>
                        </div>
                        <div className="academy-stat-item">
                            <span className="academy-stat-label">Waitlist</span>
                            <span className="academy-stat-value status-waitlist">
                                <span className="academy-stat-value-indicator status-waitlist"></span>
                                {stats.waitlist}
                            </span>
                        </div>
                        <div className="academy-stat-item">
                            <span className="academy-stat-label">Rejected</span>
                            <span className="academy-stat-value status-rejected">
                                <span className="academy-stat-value-indicator status-rejected"></span>
                                {stats.rejected}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="academy-stat-card">
                    <div className="academy-stat-title">Top Countries</div>
                    <div className="academy-stat-content">
                        {Object.entries(stats.byCountry)
                            .sort(([,a], [,b]) => b - a)
                            .slice(0, 5)
                            .map(([countryCode, count]) => (
                                <div key={countryCode} className="academy-stat-item">
                                    <span className="academy-stat-label">{getCountryName(countryCode)}</span>
                                    <span className="academy-stat-value">{count}</span>
                                </div>
                            ))
                        }
                    </div>
                </div>

                <div className="academy-stat-card">
                    <div className="academy-stat-title">Education Level Distribution</div>
                    <div className="academy-stat-content">
                        {Object.entries(stats.byEducationLevel)
                            .sort(([,a], [,b]) => b - a)
                            .map(([level, count]) => (
                                <div key={level} className="academy-stat-item">
                                    <span className="academy-stat-label">{level}</span>
                                    <span className="academy-stat-value">{count}</span>
                                </div>
                            ))
                        }
                    </div>
                </div>
                
                <div className="academy-stat-card">
                    <div className="academy-stat-title">Age Statistics</div>
                    <div className="academy-stat-content">
                        <div className="academy-stat-item">
                            <span className="academy-stat-label">Average Age</span>
                            <span className="academy-stat-value">{stats.averageAge}</span>
                        </div>
                        <div className="academy-stat-item">
                            <span className="academy-stat-label">Minimum Age</span>
                            <span className="academy-stat-value">{stats.minAge}</span>
                        </div>
                        <div className="academy-stat-item">
                            <span className="academy-stat-label">Maximum Age</span>
                            <span className="academy-stat-value">{stats.maxAge}</span>
                        </div>
                    </div>
                </div>

                <div className="academy-stat-card">
                    <div className="academy-stat-title">Gender Distribution</div>
                    <div className="academy-stat-content">
                        {Object.entries(stats.genderDistribution).length > 0 ? (
                            Object.entries(stats.genderDistribution)
                                .sort(([, a], [, b]) => b - a)
                                .map(([gender, count]) => (
                                    <div key={gender} className="academy-stat-item">
                                        <span className="academy-stat-label">{gender}</span>
                                        <span className="academy-stat-value">{count}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="academy-stat-item">
                                    <span className="academy-stat-label">No gender data available</span>
                                </div>
                            )
                        }
                    </div>
                </div>

                <div className="academy-stat-card">
                    <div className="academy-stat-title">Top Fields of Study</div>
                    <div className="academy-stat-content">
                        {stats.topFieldsOfStudy.length > 0 ? (
                            stats.topFieldsOfStudy.map(item => (
                                <div key={item.field} className="academy-stat-item">
                                    <span className="academy-stat-label">{item.field}</span>
                                    <span className="academy-stat-value">{item.count}</span>
                                </div>
                            ))
                            ) : (
                                <div className="academy-stat-item">
                                    <span className="academy-stat-label">No field of study data</span>
                                </div>
                            )
                        }
                    </div>
                </div>
                
                <div className="academy-stat-card">
                    <div className="academy-stat-title">Acceptance Rate</div>
                    <div className="academy-stat-content">
                         <div className="academy-stat-item">
                             <span className="academy-stat-label">Current Rate</span>
                             <span className="academy-stat-value highlight">{stats.acceptanceRate}%</span>
                         </div>
                         <div className="academy-stat-item-note">
                             <small>(Based on {stats.approved} approved out of {stats.total - stats.pending} considered applications)</small>
                         </div>
                    </div>
                </div>

                <div className="academy-stat-card">
                    <div className="academy-stat-title">Recent Applications</div>
                    <div className="academy-stat-content">
                        {stats.recentApplications.map(app => (
                            <div key={app.id} className="academy-recent-application">
                                <div className="academy-recent-app-header">
                                    <span className="academy-recent-app-name">
                                        {app.firstName} {app.lastName}
                                    </span>
                                    <span className={`academy-status-badge status-${app.status || 'pending'}`}>
                                        {app.status || 'pending'}
                                    </span>
                                </div>
                                <div className="academy-recent-app-date">
                                    {formatDate(app.createdAt)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const filteredApplications = applications
        .filter(app => 
            (filter === 'all' || app.status === filter) &&
            (searchTerm === '' || 
             `${app.firstName} ${app.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
             app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
             (app.country && getCountryName(app.country).toLowerCase().includes(searchTerm.toLowerCase())))
        )
        .sort((a, b) => {
            if (sortBy === 'submissionDate') {
                return sortOrder === 'desc' 
                    ? new Date(b.createdAt) - new Date(a.createdAt)
                    : new Date(a.createdAt) - new Date(b.createdAt);
            } else if (sortBy === 'name') {
                return sortOrder === 'desc'
                    ? `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`)
                    : `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
            } else if (sortBy === 'status') {
                return sortOrder === 'desc'
                    ? (b.status || 'pending').localeCompare(a.status || 'pending')
                    : (a.status || 'pending').localeCompare(b.status || 'pending');
            }
            return 0;
        });

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    if (loading) {
        return (
            <div className="academy-loading-message">
                <div className="academy-loading-spinner"></div>
                <p>Loading applications...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="academy-error-message">
                <h3>Error</h3>
                <p>{error}</p>
                <button onClick={fetchData}>Try Again</button>
            </div>
        );
    }

    return (
        <div className="academy-applications">
            <div className="academy-applications-header">
                <div className="academy-header-content">
                    <h1 className="academy-header-title">Academy Applications</h1>
                    <p className="header-description">
                        Manage and review applications for the Mediterranean Academy program
                    </p>
                    <Link to="/admin" className="academy-back-button">
                        <FaArrowLeft /> Back to Admin
                    </Link>
                    <div className="academy-filter-controls">
                        <div className="academy-search-container">
                            <FaSearch className="academy-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or country..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="academy-search-input"
                            />
                        </div>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="academy-status-filter"
                        >
                            <option value="all">All Applications</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="waitlist">Waitlist</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>
            </div>

            {renderStats()}

            {loading ? (
                <div className="academy-loading-message">
                    <div className="academy-loading-spinner"></div>
                    <p>Loading applications...</p>
                </div>
            ) : error ? (
                <div className="academy-error-message">
                    <h3>Error Loading Applications</h3>
                    <p>{error}</p>
                    <button onClick={fetchData}>Retry</button>
                </div>
            ) : filteredApplications.length === 0 ? (
                <div className="academy-no-applications">
                    <FaGraduationCap style={{ fontSize: '3rem', color: 'var(--primary)' }} />
                    <h2>No Applications Found</h2>
                    {filter !== 'all' ? (
                        <p>No applications with status: {filter}</p>
                    ) : searchTerm ? (
                        <p>No applications match your search criteria</p>
                    ) : (
                        <p>There are no applications in the system yet</p>
                    )}
                </div>
            ) : (
                <div className="academy-applications-container">
                    <div className="academy-applications-list">
                        <div className="academy-applications-header-row">
                            <div className="academy-header-cell" onClick={() => toggleSort('name')}>
                                Applicant
                                {sortBy === 'name' && (
                                    <FaSort className={sortOrder === 'asc' ? 'academy-sort-asc' : 'academy-sort-desc'} />
                                )}
                            </div>
                            <div className="academy-header-cell" onClick={() => toggleSort('submissionDate')}>
                                Submission Date
                                {sortBy === 'submissionDate' && (
                                    <FaSort className={sortOrder === 'asc' ? 'academy-sort-asc' : 'academy-sort-desc'} />
                                )}
                            </div>
                            <div className="academy-header-cell" onClick={() => toggleSort('status')}>
                                Status
                                {sortBy === 'status' && (
                                    <FaSort className={sortOrder === 'asc' ? 'academy-sort-asc' : 'academy-sort-desc'} />
                                )}
                            </div>
                            <div className="academy-header-cell">
                                Actions
                            </div>
                        </div>
                        {filteredApplications.map(application => (
                            <ApplicationCard
                                key={application.id}
                                application={application}
                                isSelected={selectedApplication?.id === application.id}
                                onSelect={() => setSelectedApplication(application)}
                                onDelete={handleDeleteApplication}
                                onStatusUpdate={handleStatusUpdate}
                                formatDate={formatDate}
                                getCountryName={getCountryName}
                                canDelete={canDeleteApplications}
                                currentUserRole={currentUser?.role}
                            />
                        ))}
                    </div>

                    {selectedApplication && (
                        <ApplicationDetails
                            application={selectedApplication}
                            onStatusUpdate={handleStatusUpdate}
                            onClose={() => setSelectedApplication(null)}
                            onDelete={handleDeleteApplication}
                            formatDate={formatDate}
                            calculateAge={calculateAge}
                            getCountryName={getCountryName}
                            canDelete={canDeleteApplications}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function ApplicationCard({ application, isSelected, onSelect, onDelete, onStatusUpdate, formatDate, getCountryName, canDelete, currentUserRole }) {
    const handleDelete = (e) => {
        e.stopPropagation();
        onDelete(application.id);
    };

    return (
        <div className={`academy-application-card ${isSelected ? 'selected' : ''}`} onClick={onSelect}>
            <div className="academy-applicant-info">
                <div className="admin-academy-applicant-avatar">
                    {application.photoURL ? (
                        <img src={application.photoURL} alt={`${application.firstName} ${application.lastName}`} />
                    ) : (
                        <div className="admin-academy-avatar-placeholder">
                            {application.firstName ? application.firstName[0] : 'A'}
                        </div>
                    )}
                </div>
                <div className="academy-applicant-basic-info">
                    <h3>{application.firstName} {application.lastName}</h3>
                    <p>{application.email}</p>
                </div>
            </div>
            <div className="academy-application-summary">
                <div className="academy-summary-item">
                    <span className="label">Submitted</span>
                    <span>{formatDate(application.createdAt)}</span>
                </div>
            </div>
            <div className="academy-application-status">
                <span className={`academy-status-badge status-${application.status || 'pending'}`}>
                    {application.status || 'pending'}
                </span>
            </div>
            <div className="academy-application-actions">
                <button onClick={() => onSelect(application)} className="academy-btn academy-btn-view">View</button>
                <button onClick={() => onStatusUpdate(application.id, 'approved')} className="academy-btn academy-btn-approve">Approve</button>
                <button onClick={() => onStatusUpdate(application.id, 'rejected')} className="academy-btn academy-btn-reject">Reject</button>
                <button onClick={() => onStatusUpdate(application.id, 'waitlist')} className="academy-btn academy-btn-waitlist">Waitlist</button>
                {currentUserRole === ROLES.SUPER_ADMIN && (
                    <button onClick={(e) => handleDelete(e)} className="academy-btn academy-btn-delete">Delete</button>
                )}
            </div>
        </div>
    );
}

function ApplicationDetails({ application, onStatusUpdate, onClose, onDelete, formatDate, calculateAge, getCountryName, canDelete }) {
    const handleDelete = () => {
        onDelete(application.id);
    };

    return (
        <div className="academy-application-details-panel">
            <div className="academy-details-header">
                <h2>Application Details</h2>
                <button className="academy-close-button" onClick={onClose} title="Close">
                    <i className="fas fa-times"></i>
                </button>
            </div>
            <div className="academy-details-content">
                <div className="academy-profile-header">
                    <div className="admin-academy-profile-image">
                        {application.photoURL ? (
                            <img src={application.photoURL} alt={`${application.firstName} ${application.lastName}`} />
                        ) : (
                            <div className="avatar-placeholder large">
                                {application.firstName ? application.firstName[0] : 'A'}
                            </div>
                        )}
                    </div>
                    <div className="academy-profile-info">
                        <h3>{application.firstName} {application.lastName}</h3>
                        <p>{application.email}</p>
                        <p>{application.country ? getCountryName(application.country) : 'Location not specified'}</p>
                        <span className={`academy-status-badge status-${application.status || 'pending'}`}>
                            {application.status || 'pending'}
                        </span>
                    </div>
                </div>

                <div className="academy-scrollable-content">
                    <div className="academy-details-section">
                        <h3>Personal Information</h3>
                        <div className="academy-info-grid">
                            <div className="academy-info-item">
                                <label>Date of Birth</label>
                                <span>{application.dateOfBirth ? formatDate(application.dateOfBirth).split(',')[0] : 'Not provided'}</span>
                            </div>
                            <div className="academy-info-item">
                                <label>Age</label>
                                <span>{application.dateOfBirth ? calculateAge(application.dateOfBirth) : 'N/A'}</span>
                            </div>
                            <div className="academy-info-item">
                                <label>Gender</label>
                                <span>{application.gender || 'Not specified'}</span>
                            </div>
                            <div className="academy-info-item">
                                <label>Phone</label>
                                <span>{application.phone || 'Not provided'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="academy-details-section">
                        <h3>Educational Background</h3>
                        <div className="academy-info-grid">
                            <div className="academy-info-item">
                                <label>Education Level</label>
                                <span>{application.educationLevel || 'Not specified'}</span>
                            </div>
                            <div className="academy-info-item">
                                <label>Field of Study</label>
                                <span>{application.fieldOfStudy || 'Not specified'}</span>
                            </div>
                            <div className="academy-info-item">
                                <label>Institution</label>
                                <span>{application.institution || 'Not specified'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="academy-details-section">
                        <h3>Application Essays</h3>
                        <div className="academy-info-item">
                            <label>Motivation</label>
                            <span>{application.motivation || 'Not provided'}</span>
                        </div>
                        <div className="academy-info-item">
                            <label>Goals</label>
                            <span>{application.goals || 'Not provided'}</span>
                        </div>
                    </div>

                    <div className="academy-details-section">
                        <h3>Documents</h3>
                        <div className="academy-document-links">
                            {application.resume ? (
                                <a 
                                    href={application.resume} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="academy-document-link"
                                >
                                    <span className="academy-document-icon"><i className="fas fa-file-pdf"></i></span>
                                    <span>Resume/CV</span>
                                </a>
                            ) : (
                                <div className="academy-no-document">No resume/CV uploaded</div>
                            )}
                            
                            {application.motivationLetter ? (
                                <a 
                                    href={application.motivationLetter} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="academy-document-link"
                                >
                                    <span className="academy-document-icon"><i className="fas fa-file-alt"></i></span>
                                    <span>Motivation Letter</span>
                                </a>
                            ) : (
                                <div className="academy-no-document">No motivation letter uploaded</div>
                            )}
                        </div>
                    </div>

                    <div className="academy-details-section">
                        <h3>Submission Information</h3>
                        <div className="academy-info-grid">
                            <div className="academy-info-item">
                                <label>Submitted On</label>
                                <span>{formatDate(application.createdAt)}</span>
                            </div>
                            <div className="academy-info-item">
                                <label>Application ID</label>
                                <span>{application.id}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="academy-action-buttons">
                    <button 
                        className="academy-approve-button" 
                        onClick={() => onStatusUpdate(application.id, 'approved')}
                        disabled={application.status === 'approved'}
                    >
                        <i className="fas fa-check-circle"></i> Approve
                    </button>
                    <button 
                        className="academy-waitlist-button" 
                        onClick={() => onStatusUpdate(application.id, 'waitlist')}
                        disabled={application.status === 'waitlist'}
                    >
                        <i className="fas fa-clock"></i> Waitlist
                    </button>
                    <button 
                        className="academy-reject-button" 
                        onClick={() => onStatusUpdate(application.id, 'rejected')}
                        disabled={application.status === 'rejected'}
                    >
                        <i className="fas fa-times-circle"></i> Reject
                    </button>
                    {canDelete && (
                        <button 
                            className="academy-delete-button" 
                            onClick={handleDelete}
                        >
                            <i className="fas fa-trash"></i> Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AcademyApplications; 